import { Button } from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import Flex from "../../../../../components/Flex";
import GlobalFilter from "../../../../../components/GlobalFilter";
import MaskMultiSelectScroll from "../../../../../components/MaskMultiSelectScroll";
import { ScrollArea } from "../../../../../components/ScrollBar";
import Text from "../../../../../components/Text";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { useAppointmentTypeGetAll } from "../../../../../queries/practice-setting/appointment-type-configuration/use-appoinment-type-get-all";
import { useProvidersList } from "../../../../../queries/provider/setting/use-providers-list";
// import { usePatientsList } from "../../../../../queries/use-get-patients";
// import { useLocationsList } from "../../../../../queries/use-locationList";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import { Filter } from "./OpenEncounters";
import { useActiveComponentCountController } from "../../../../../components/filter/use-ActiveComponentCountController";

interface OpenEncounterFilterProps {
  onApply: (v: any) => void;
  defaultPreFilters?: Filter;
}

const initialValues = {
  appointment_type: undefined,
  date_of_service: undefined,
  location: undefined,
  providers: undefined,
  patients: undefined,
};

const OpenEncounterFilter = ({
  onApply,
  defaultPreFilters,
}: OpenEncounterFilterProps) => {
  const activeFilterCount = useActiveComponentCountController(
    (defaultPreFilters as any) ?? {}
  );
  const formik = useFormik({
    initialValues: defaultPreFilters || initialValues,
    enableReinitialize: true,
    onSubmit: (v) => {
      onApply(v);
    },
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [providerSearchKey, setProviderSearchKey] = useDebounce<string>("");
  // const [locationSearchKey, setLocationSearchKey] = useDebounce<string>("");
  const [providerPagination, setProviderPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: providersList, isFetching: isProvidersFetching } =
    useProvidersList({
      pagination: providerPagination,
      searchKey: providerSearchKey,
      xTENANTID: getXtenantId(),
      enabled: !!anchorEl,
    });

  const providerOptions = useMemo(() => {
    const fetched =
      providersList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.firstName + " " + it?.lastName,
      })) ?? [];
    const selected = (formik.values.providers ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [providersList?.data?.content, formik.values.providers]);

  const [appointmentTypeSearchKey, setAppointmentTypeSearchKey] =
    useDebounce<string>("");
  const [appointmentTypePagination, setAppointmentTypePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: appointmentTypeList, isFetching: isAppointmentTypeFetching } =
    useAppointmentTypeGetAll({
      pagination: appointmentTypePagination,
      searchString: appointmentTypeSearchKey,
      status: true,
      archive: false,
      xTENANTID: getXtenantId(),
    });

  const appointmentTypeOptions = useMemo(() => {
    const fetched =
      appointmentTypeList?.data?.content
        ?.filter((it: any) => it?.typeName)
        ?.map((it: any) => ({
          value: it?.typeName,
          label: it?.nameString ?? it?.typeName,
        })) ?? [];
    const selected = (formik.values.appointment_type ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) =>
        !fetched.some(
          (o: { [k: string]: string }) => o[idKey] === s[idKey]
        )
    );
    return [...missing, ...fetched];
  }, [appointmentTypeList?.data?.content, formik.values.appointment_type]);

  // const [patientSearchKey, setPatientSearchKey] = useDebounce<string>("");
  // const { data: patientList } = usePatientsList({
  //   searchKey: patientSearchKey,
  //   pagination: DEF_PAGINATION,
  //   providerGroupId: getXtenantId(),
  // });

  const [expanded, setExpanded] = useState<{ [k: string]: boolean }[]>(
    Object.keys(initialValues).map((it) => ({ [it]: false }))
  );

  function handleExpandClick(k: string, value: boolean) {
    setExpanded((prev) =>
      prev?.map((it) => {
        const curKey = Object.keys(it)[0];

        return Object.keys(it).some((obj) => obj === k)
          ? { [k]: value }
          : { [curKey]: false };
      })
    );
  }

  // const { data: locations } = useLocationsList({
  //   pagination: DEF_PAGINATION,
  //   searchKey: locationSearchKey,
  //   providerGroupId: getXtenantId(),
  // });

  // const transLocations = useMemo(() => {
  //   return locations?.data?.content?.map((it) => ({
  //     value: it.uuid,
  //     label: it?.name,
  //   }));
  // }, [locations]);

  const closeFilter = () => setAnchorEl(null);

  return (
    <GlobalFilter
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      activeFilterCount={activeFilterCount}
    >
      <Flex
        width={"18.438rem"}
        flexDirection={"column"}
        justifyContent={"space-between"}
      >
        <Flex width={"100%"} justifyContent={"end"}>
          <Button
            variant="text"
            onClick={() => {
              formik.resetForm();
              formik.submitForm();
              onApply(initialValues);
            }}
          >
            Clear Filter
          </Button>
        </Flex>
        <ScrollArea height={"80%"}>
          {Object.keys(initialValues).map((key, ind) => {
            return key === "appointment_type" ? (
              <Accordion
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>Appointment Type</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <MaskMultiSelectScroll
                    onSearch={(val) => setAppointmentTypeSearchKey(val)}
                    optionWithCheckbox={true}
                    value={formik.values.appointment_type}
                    placeholder="Appointment Type"
                    // The encounter list API filters on the appointment type
                    // key, so the option keeps sending `typeName` while showing
                    // the configured `nameString`.
                    options={appointmentTypeOptions}
                    onChange={(val) => {
                      formik.setFieldValue("appointment_type", val);
                    }}
                    pagination={appointmentTypePagination}
                    setPagination={setAppointmentTypePagination}
                    totalElements={
                      appointmentTypeList?.data?.page?.totalElements
                    }
                    loading={isAppointmentTypeFetching}
                  />
                </AccordionDetails>
              </Accordion>
            ) : key === "date_of_service" ? (
              <Accordion
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>Visit Date</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <CustomDatePicker
                    placeholder="Visit Date"
                    value={formik.values.date_of_service}
                    onChange={(v) =>
                      formik.setFieldValue("date_of_service", v?.toISOString())
                    }
                  />
                </AccordionDetails>
              </Accordion>
            ) : // : key === "location" ? (
            //   <Accordion
            //     variant="outlined"
            //     expanded={expanded[ind][key]}
            //     onChange={(_, newExpanded) =>
            //       handleExpandClick(key, newExpanded)
            //     }
            //   >
            //     <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            //       <Text>Locations</Text>
            //     </AccordionSummary>
            //     <AccordionDetails sx={{ padding: 1 }}>
            //       <MaskMultiSelect
            //         onSearch={(val) => setLocationSearchKey(val)}
            //         optionWithCheckbox={true}
            //         value={formik.values.location}
            //         placeholder="Locations"
            //         options={transLocations}
            //         onChange={(val) => formik.setFieldValue("location", val)}
            //       />
            //     </AccordionDetails>
            //   </Accordion>
            // )
            // key === "patients" ? (
            //   <Accordion
            //     variant="outlined"
            //     expanded={expanded[ind][key]}
            //     onChange={(_, newExpanded) =>
            //       handleExpandClick(key, newExpanded)
            //     }
            //   >
            //     <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            //       <Text>Patient</Text>
            //     </AccordionSummary>
            //     <AccordionDetails sx={{ padding: 1 }}>
            //       <MaskMultiSelect
            //         onSearch={(val) => setPatientSearchKey(val)}
            //         optionWithCheckbox={true}
            //         value={formik.values.patients}
            //         placeholder="Patients"
            //         options={patientList?.data?.content?.map((it) => ({
            //           value: it?.uuid,
            //           label: it?.firstName + " " + it?.lastName,
            //         }))}
            //         onChange={(val) => formik.setFieldValue("patients", val)}
            //       />
            //     </AccordionDetails>
            //   </Accordion>
            // ) :
            key === "providers" ? (
              <Accordion
                variant="outlined"
                expanded={expanded[ind][key]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(key, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{replaceUnderScoreAddSpace(key)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <MaskMultiSelectScroll
                    onSearch={(val) => setProviderSearchKey(val)}
                    optionWithCheckbox={true}
                    value={formik.values.providers}
                    placeholder="Providers"
                    options={providerOptions}
                    onChange={(val) => {
                      formik.setFieldValue("providers", val);
                    }}
                    pagination={providerPagination}
                    setPagination={setProviderPagination}
                    totalElements={providersList?.data?.page?.totalElements}
                    loading={isProvidersFetching}
                  />
                </AccordionDetails>
              </Accordion>
            ) : (
              <></>
            );
          })}
        </ScrollArea>
        <Flex width={"100%"} justifyContent={"end"}>
          <Button
            variant="text"
            sx={{ color: "gray" }}
            onClick={() => {
              formik.resetForm();
              formik.submitForm();
              closeFilter();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={() => {
              formik.submitForm();
              closeFilter();
            }}
          >
            Apply
          </Button>
        </Flex>
      </Flex>
    </GlobalFilter>
  );
};

export default OpenEncounterFilter;
